// src/components/AuthForm.js
import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './AuthForm.css';

import logo from '../LogoBanaprecios.png';

const API_URL = process.env.REACT_APP_API_URL;

const AuthForm = ({ onAuthSuccess, onShowQs, initialMode = 'login' }) => {
  const { t } = useTranslation();
  const [mode, setMode]             = useState(initialMode);
  const [username, setUsername]     = useState('');
  const [password, setPassword]    = useState('');
  const [email, setEmail]          = useState('');
  const [firstName, setFirstName]  = useState('');
  const [lastName, setLastName]    = useState('');
  const [birthDate, setBirthDate]  = useState('');
  const [marketRole, setMarketRole]     = useState('');
  const [companyName, setCompanyName]   = useState('');
  const [phone, setPhone]               = useState('');
  const [error, setError]          = useState('');

  const resetSignupFields = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setBirthDate('');
    setMarketRole('');
    setCompanyName('');
    setPhone('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const url = mode === 'login'
      ? `${API_URL}/auth/token/`
      : `${API_URL}/register/`;

    const payload = { username, password };
    if (mode === 'signup') {
      payload.email        = email;
      payload.first_name   = firstName.trim();
      payload.last_name    = lastName.trim();
      payload.birth_date   = birthDate;
      payload.market_role  = marketRole;
      payload.company_name = companyName.trim();
      payload.phone        = phone.trim();
    }

    try {
      const response = await axios.post(url, payload);

      if (mode === 'login') {
        const { access, refresh } = response.data;
        localStorage.setItem('access_token', access);
        localStorage.setItem('refresh_token', refresh);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;
        onAuthSuccess();
      } else {
        alert(t('signup_success'));
        setMode('login');
        setUsername('');
        setPassword('');
        resetSignupFields();
      }
    } catch (err) {
      console.error(err);
      const data = err.response?.data;
      let errorMsg;
      if (data) {
        if (data.detail) {
          errorMsg = data.detail;
        } else {
          // Field-level errors — show the first one
          const firstKey = Object.keys(data)[0];
          const val = data[firstKey];
          errorMsg = Array.isArray(val) ? val[0] : val;
        }
      }
      setError(errorMsg || t(mode === 'login' ? 'login_error' : 'signup_error'));
    }
  };

  const switchMode = () => {
    setError('');
    setUsername('');
    setPassword('');
    resetSignupFields();
    setMode(mode === 'login' ? 'signup' : 'login');
  };

  return (
    <div className="auth-container">
      <div className="auth-form-card">
        <div className="auth-logo-container">
          <img src={logo} alt="BanaPrecios Logo" />
        </div>

        <h2>{mode === 'login' ? '🔐 ' + t('login_title') : '📝 ' + t('signup_title')}</h2>

        <form onSubmit={handleSubmit} noValidate>
          {/* ── Signup-only fields ─────────────────────────────── */}
          {mode === 'signup' && (
            <>
              <div className="auth-field-row">
                <label htmlFor="firstName">
                  {t('first_name')}
                  <input
                    id="firstName"
                    type="text"
                    placeholder={t('first_name')}
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    required
                    autoComplete="given-name"
                  />
                </label>
                <label htmlFor="lastName">
                  {t('last_name')}
                  <input
                    id="lastName"
                    type="text"
                    placeholder={t('last_name')}
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    required
                    autoComplete="family-name"
                  />
                </label>
              </div>

              <label htmlFor="email">
                {t('email')}
                <input
                  id="email"
                  type="email"
                  placeholder={t('email')}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </label>

              <label htmlFor="birthDate">
                {t('birth_date')}
                <input
                  id="birthDate"
                  type="date"
                  value={birthDate}
                  onChange={e => setBirthDate(e.target.value)}
                  required
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 13))
                    .toISOString().split('T')[0]}
                />
              </label>

              <div className="auth-section-divider">
                <span>{t('reg_market_section')}</span>
              </div>

              <label htmlFor="marketRole">
                {t('reg_market_role')}
                <select
                  id="marketRole"
                  value={marketRole}
                  onChange={e => setMarketRole(e.target.value)}
                >
                  <option value="">{t('reg_role_none')}</option>
                  <option value="productor">{t('mkt_producer')}</option>
                  <option value="exportadora">{t('mkt_exporter')}</option>
                  <option value="comercial">{t('mkt_commercial')}</option>
                </select>
              </label>

              {marketRole && (
                <div className="auth-field-row">
                  <label htmlFor="companyName">
                    {t('reg_company')}
                    <input
                      id="companyName"
                      type="text"
                      placeholder={t('reg_company_placeholder')}
                      value={companyName}
                      onChange={e => setCompanyName(e.target.value)}
                      autoComplete="organization"
                    />
                  </label>
                  <label htmlFor="phone">
                    {t('reg_phone')}
                    <input
                      id="phone"
                      type="tel"
                      placeholder="+593..."
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      autoComplete="tel"
                    />
                  </label>
                </div>
              )}
            </>
          )}

          {/* ── Always visible ─────────────────────────────────── */}
          <label htmlFor="username">
            {t('username')}
            <input
              id="username"
              type="text"
              placeholder={t('username')}
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
              autoComplete="username"
            />
          </label>

          <label htmlFor="password">
            {t('password')}
            <input
              id="password"
              type="password"
              placeholder={t('password')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            />
          </label>

          <button type="submit" className="auth-button">
            {mode === 'login' ? '🚀 ' + t('login_button') : '✨ ' + t('signup_button')}
          </button>
        </form>

        {error && <p className="error" role="alert">{error}</p>}

        <div className="switch-mode">
          {mode === 'login' ? t('no_account') : t('have_account')}{' '}
          <button type="button" className="link-button" onClick={switchMode}>
            {mode === 'login' ? t('signup_here') : t('login_here')}
          </button>
        </div>

        {onShowQs && (
          <div className="auth-qs-teaser">
            <span>💬</span>
            <span>¿Quieres ver las opiniones del mercado?</span>
            <button type="button" className="link-button" onClick={onShowQs}>
              Ver Q's sin registrarte →
            </button>
          </div>
        )}

        <div className="auth-info-section">
          <div className="auth-features">
            <h4>🌿 Servicios para el Sector Bananero</h4>
            <ul>
              <li>Análisis de mercado en tiempo real</li>
              <li>Seguimiento de tendencias de precios</li>
              <li>Reportes históricos detallados</li>
              <li>Herramientas de comparación de mercado</li>
              <li>Información para productores y exportadores</li>
            </ul>
          </div>
          <div className="auth-contact">
            <p>
              🤝 <strong>Contacto Comercial:</strong><br />
              Para consultas del sector agrícola:{' '}
              <span
                className="green-email"
                onClick={() => { navigator.clipboard.writeText('info.banaprecios@gmail.com'); alert(t('email_copied')); }}
                title={t('click_to_copy')}
              >
                info.banaprecios@gmail.com
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;
